// Copyright (c) 2021 JCA Electronics , Winnipeg, MB. All rights reserved.

import { SinglePathMission } from '../models/SinglePathMission';
import { SimplePoint } from '../models/SimplePoint';
import { Point } from '../models/Point';
import RenderColors from './colors';
import { AreaPolygon } from '../models/AreaPolygon';
import { IPolylineOptions } from '../models/IPolylineOptions';
import { LeafletMarkerOptions } from '../models/MachineMarkerLeafletOptions';
import * as L from 'leaflet';
import { LeafletRenderHelper } from './LeafletRenderHelper';

export class RenderHelper {
  static getBounds(path: google.maps.MVCArray<google.maps.LatLng>): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();
    path.forEach((point) => bounds.extend(point));
    return bounds;
  }

  static getFieldPolygonOptionsFromPoint(path: Point[]): google.maps.PolygonOptions {
    const points = path.map((val) => {
      return { lat: val.Item1, lng: val.Item2 };
    });
    return this.getFieldPolygonOptions(points);
  }

  static getFieldPolygonOptionsFromMission(missionPlan: SinglePathMission): google.maps.PolygonOptions {
    const points = missionPlan.FieldBoundary.DrawPoints.map((val) => {
      return { lat: val.Latitude, lng: val.Longitude };
    });
    return this.getFieldPolygonOptions(points);
  }

  static getFieldPolygonOptions(path: google.maps.LatLngLiteral[]): google.maps.PolygonOptions {
    return {
      paths: [path],
      strokeColor: RenderColors.Field,
      fillColor: 'transparent',
      strokeOpacity: 0.8,
      strokeWeight: 3
    };
  }

  static getWorkAreaPolygonOptionsFromMission(missionPlan: SinglePathMission): google.maps.PolygonOptions {
    let workArea: google.maps.LatLngLiteral[] = [];
    if (missionPlan != null && missionPlan.WorkAreas != undefined) {
      workArea = missionPlan.WorkAreas[0].map((val) => {
        return { lat: val.Lat, lng: val.Long };
      });
    }

    return this.getWorkAreaPolygonOptions(workArea);
  }

  static getWorkAreaPolygonOptionsFromSimplePoint(path: SimplePoint[]): google.maps.PolylineOptions {
    return this.getWorkAreaPolygonOptions(
      path.map((val) => {
        return { lat: val.Lat, lng: val.Long };
      })
    );
  }

  static getWorkAreaPolygonOptions(workArea: google.maps.LatLngLiteral[]): google.maps.PolygonOptions {
    return {
      paths: [workArea],
      strokeColor: 'transparent',
      fillColor: RenderColors.WorkArea,
      strokeOpacity: 0.8,
      strokeWeight: 3
    };
  }

  static getPathPolylineOptionsFromMission(missionPlan: SinglePathMission): google.maps.PolylineOptions {
    let path: google.maps.LatLngLiteral[] = [];
    if (missionPlan != null && missionPlan.Path != undefined) {
      path = missionPlan.Path.map((val) => {
        return { lat: val.Lat, lng: val.Long };
      });
    }

    return this.getPathPolylineOptions(path);
  }

  static getPathPolylineOptionsFromSimplePoint(path: SimplePoint[]): google.maps.PolylineOptions {
    return this.getPathPolylineOptions(
      path.map((val) => {
        return { lat: val.Lat, lng: val.Long };
      })
    );
  }

  static getPathPolylineOptions(path: google.maps.LatLngLiteral[]): google.maps.PolylineOptions {
    return {
      path: path,
      strokeColor: RenderColors.PlannedPath,
      strokeOpacity: 0.8,
      strokeWeight: 3
    };
  }

  static clearFields(polygons: google.maps.PolygonOptions[]) {
    return polygons.filter((x) => x.strokeColor != RenderColors.Field);
  }

  static clearPaths(polyLines: google.maps.PolylineOptions[]) {
    return polyLines.filter((x) => x.strokeColor != RenderColors.PlannedPath);
  }

  static clearWorkAreas(polygons: google.maps.PolygonOptions[]) {
    return polygons.filter((x) => x.fillColor != RenderColors.WorkArea);
  }

  static createStartMakerWithLabel(line: google.maps.Polyline, edit = false): google.maps.Marker {
    const startLabelPosition = new google.maps.Point(36, -8);
    return new google.maps.Marker({
      position: line.getPath().getAt(0),
      icon: this.createSvg('M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8Z', startLabelPosition, edit), // mdi-circle-medium svg
      label: this.createMarkerLabel('Start')
    });
  }

  static createStopMakerWithLabel(line: google.maps.Polyline, edit = false): google.maps.Marker {
    const stopLabelPosition = new google.maps.Point(32, -8);
    return new google.maps.Marker({
      position: line.getPath().getAt(line.getPath().getLength() - 1),
      icon: this.createSvg('M18,18H6V6H18V18Z', stopLabelPosition, edit), // mdi-stop svg
      label: this.createMarkerLabel('Stop')
    });
  }

  private static createMarkerLabel(text: string): google.maps.ReadonlyMarkerLabel {
    return {
      text: text.split('').join(' '),
      color: RenderColors.PlannedPath,
      fontSize: '16px',
      fontFamily: 'Roboto'
    };
  }

  private static createSvg(path: string, labelPosition: google.maps.Point, edit: boolean): google.maps.ReadonlySymbol {
    const centeredPoint = new google.maps.Point(12, 12);
    const highPoint = new google.maps.Point(12, 32);

    return {
      path: path,
      fillColor: RenderColors.PlannedPath,
      fillOpacity: 1,
      strokeWeight: 0,
      rotation: 0,
      scale: 0.8,
      anchor: edit ? highPoint : centeredPoint,
      labelOrigin: labelPosition
    };
  }

  static getPolygonOptions(path: google.maps.LatLng[]): google.maps.PolygonOptions {
    return {
      paths: [path],
      strokeColor: 'white',
      strokeOpacity: 0.8,
      strokeWeight: 6,
      fillOpacity: 0.5,
      editable: true
    };
  }

  static getPolygonOptionsFromAreaPolygon(polygons: AreaPolygon[], color: string): google.maps.PolygonOptions[] {
    const result: google.maps.PolygonOptions[] = [];

    polygons.forEach((polygon) => {
      const path: google.maps.LatLng[] = polygon.DrawPoints.map((x) => {
        return new window.google.maps.LatLng({ lat: x.Latitude, lng: x.Longitude });
      });

      const options = this.getPolygonOptions(path);
      options.fillColor = color;
      result.push(options);
    });

    return result;
  }

  static getMarkerOptions(polygonOptions: google.maps.PolygonOptions, icon: string): google.maps.MarkerOptions {
    const paths = polygonOptions.paths as google.maps.LatLng[][];
    return {
      position: RenderHelper.getLatLngArrayBoundsCenter(paths[0]),
      icon: {
        path: icon,
        fillColor: 'white',
        fillOpacity: 0.7,
        strokeWeight: 0,
        rotation: 0,
        scale: 1,
        anchor: new window.google.maps.Point(12, 12)
      }
    };
  }

  static getPolygonBoundsCenter(polygon: google.maps.Polygon): google.maps.LatLng {
    const bounds = new window.google.maps.LatLngBounds();
    polygon.getPath().forEach((point) => {
      bounds.extend(point);
    });
    return bounds.getCenter();
  }

  static getLatLngArrayBoundsCenter(polygon: google.maps.LatLng[]): google.maps.LatLng {
    const bounds = new window.google.maps.LatLngBounds();
    polygon.forEach((point) => {
      bounds.extend(point);
    });
    return bounds.getCenter();
  }

  /**
   * Leaflet helper methods have been moved to LeafletRenderHelper.ts.
   * Any new Leaflet methods should be added there.
   */

  /**
   * @deprecated Use LeafletRenderHelper.getPolygonOptions()
   */
  static getLeafletPolygonOptions(polygon: SimplePoint[], color: string): IPolylineOptions {
    return LeafletRenderHelper.getPolygonOptions(polygon, color);
  }

  /**
   * @deprecated Use LeafletRenderHelper.getBoundaryPolygonOptions()
   */
  static getLeafletBoundaryPolygonOptions(polygon: SimplePoint[], color: string): IPolylineOptions {
    return LeafletRenderHelper.getBoundaryPolygonOptions(polygon, color);
  }

  /**
   * @deprecated Use LeafletRenderHelper.getMarkerOptions()
   */
  static getLeafletMarkerOptions(polygon: SimplePoint[], icon: string): LeafletMarkerOptions {
    return LeafletRenderHelper.getMarkerOptions(polygon, icon);
  }

  /**
   * @deprecated Use LeafletRenderHelper.makeLIconFromSvgPath()
   */
  static makeLIconFromSvgPath(icon: string): L.Icon {
    return LeafletRenderHelper.makeLIconFromSvgPath(icon);
  }
}
